/**
 * Product-catalog slice — brands only.
 *
 * The 2026-07-28 dead-endpoint sweep deleted getModels/getCategories/
 * getSubcategories (zero consumers anywhere since the facets migration
 * gave search its scoped dropdowns). Brands survives because the
 * listing wizard's brand picker needs the GLOBAL catalog, which facets
 * can't provide — vendors pick any brand, not just ones present in
 * current search results. Schema-validated end-to-end; tier B .ts so
 * the hook types flow.
 */
import { createApi } from "@reduxjs/toolkit/query/react";
import type { z } from "zod";
import {
  PRODUCT_ROUTES,
  BrandsEnvelopeSchema,
  BrandsResponseSchema,
  unwrapBrands,
  IncludedInBoxSchema,
  IncludedInBoxEnvelopeSchema,
  unwrapIncludedInBox,
} from "@gearghost/api-schemas/listing";
import axiosBaseQuery from "./axiosBaseQuery";
import { onSchemaFailure } from "./onSchemaFailure";

export const productApi = createApi({
  reducerPath: "productApi",
  baseQuery: axiosBaseQuery(),
  onSchemaFailure,
  tagTypes: ["Brands"],
  endpoints: (builder) => ({
    getBrands: builder.query<z.infer<typeof BrandsResponseSchema>, void>({
      query: () => PRODUCT_ROUTES.brands,
      rawResponseSchema: BrandsEnvelopeSchema,
      transformResponse: unwrapBrands,
      responseSchema: BrandsResponseSchema,
      providesTags: ["Brands"],
    }),
    // Included-in-box pre-fill for the inventory form. Replaced a raw
    // axiosInstance.get in InventoryForm — the last unschema'd data read
    // in the inventory flow.
    getModelIncludedInBox: builder.query<
      z.infer<typeof IncludedInBoxSchema>,
      number | string
    >({
      query: (modelId) => PRODUCT_ROUTES.modelIncludedInBox(modelId),
      rawResponseSchema: IncludedInBoxEnvelopeSchema,
      transformResponse: unwrapIncludedInBox,
      responseSchema: IncludedInBoxSchema,
    }),
  }),
});

export const { useGetBrandsQuery, useLazyGetModelIncludedInBoxQuery } =
  productApi;
